import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Tooltip as MuiTooltip } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
  ResponsiveContainer
} from 'recharts';
import { TimeSeriesData, PrevisionData, PredictionBreakdown, ImpactedArticles } from '../types/prevision';
import { Article } from '../types/api.types';
import correctionsService from '../services/correctionsService';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import api from '../services/api';
import { API_ROUTES } from '../config/api';
import {
  formatModelQuality,
  MODEL_QUALITY_LABEL,
  MODEL_QUALITY_TOOLTIP,
} from '../utils/modelQuality';

interface PrevisionViewProps {
  data: PrevisionData;
  selectedArticle?: Article;
  selectedModel?: string | null;
  disabledModels?: Set<string>; // Optional, defaults to empty Set
  onModelDataRefetch?: () => void; // Callback to trigger model data refetch
  colorSeed?: string | number; // Seed to tune generated colors for unknown models
  corrections?: Array<{ month: string; value: number | null; type?: string }>;
  onCorrectionChange?: (corrections: Array<{ month: string; value: number | null; type?: string }>) => void;
  filters?: any; // Filters to apply when fetching raw predictions
}



// Color palette for models
const MODEL_COLORS = {
  historical: '#6366f1',  // indigo-500 (HSL: ~238°)
};

// Historical color hue range to avoid (blue/indigo range)
const HISTORICAL_HUE_MIN = 220; // Start of blue range
const HISTORICAL_HUE_MAX = 260; // End of blue range
const HISTORICAL_HUE_RANGE = HISTORICAL_HUE_MAX - HISTORICAL_HUE_MIN; // 40 degrees
const AVAILABLE_HUE_RANGE = 360 - HISTORICAL_HUE_RANGE; // 320 degrees available

// Deterministic color assignment for unknown model IDs
function hashStringToInt(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    // 31 hash
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

function getDeterministicColorFromString(input: string, seed: string | number): string {
  const combined = `${input}#${String(seed)}`;
  const hash = hashStringToInt(combined);
  // Generate hue in the full 360 range first
  let hue = hash % 360;
  
  // If hue falls in the forbidden blue range, remap it to avoid similarity with historical color
  if (hue >= HISTORICAL_HUE_MIN && hue <= HISTORICAL_HUE_MAX) {
    // Remap the forbidden range (40 degrees) across the available range (320 degrees)
    // This maintains determinism while avoiding blue colors
    const offsetInForbiddenRange = hue - HISTORICAL_HUE_MIN;
    // Distribute across available ranges: before and after the forbidden range
    const remappedPosition = (offsetInForbiddenRange / HISTORICAL_HUE_RANGE) * AVAILABLE_HUE_RANGE;
    
    // Split remapping: first half goes to range before (0-220), second half goes to range after (260-360)
    if (remappedPosition < AVAILABLE_HUE_RANGE / 2) {
      // Map to range 0-220
      hue = (remappedPosition / (AVAILABLE_HUE_RANGE / 2)) * HISTORICAL_HUE_MIN;
    } else {
      // Map to range 260-360
      const positionInSecondHalf = remappedPosition - (AVAILABLE_HUE_RANGE / 2);
      hue = HISTORICAL_HUE_MAX + (positionInSecondHalf / (AVAILABLE_HUE_RANGE / 2)) * (360 - HISTORICAL_HUE_MAX);
    }
  }
  
  const saturation = 65; // %
  const lightness = 52;  // %
  return `hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`;
}

function getModelColor(modelId: string, seed: string | number): string {
  const predefined = (MODEL_COLORS as Record<string, string>)[modelId];
  if (predefined) return predefined;
  return getDeterministicColorFromString(modelId, seed);
}

// Custom dot: square if has_correction, else circle, only if value exists
const CustomDot = (props: any) => {
  const { cx, cy, payload, stroke, fill, r = 6, dataKey } = props;
  const value = payload[dataKey];
  
  // Always return a valid element, even if transparent
  if (value === undefined || value === null) {
    return <circle cx={cx} cy={cy} r={0} fill="transparent" stroke="transparent" />;
  }
  
  // Only show square for corrected predictions (not raw)
  if (payload.has_correction && !dataKey.includes('Raw')) {
    // Square for corrected points with enhanced visual styling
    return (
      <rect
        x={cx - r}
        y={cy - r}
        width={2 * r}
        height={2 * r}
        fill="#3b82f6" // Blue fill for corrections
        stroke="#fff"
        strokeWidth={3}
        rx={2}
        ry={2}
        style={{ filter: 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))' }}
      />
    );
  }
  
  // Default: circle
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={fill}
      stroke={stroke}
      strokeWidth={2}
    />
  );
};

const ModelQualityTooltipLine = ({ value }: { value: number | null | undefined }) => (
  <p className="text-xs text-gray-500" title={MODEL_QUALITY_TOOLTIP}>
    {MODEL_QUALITY_LABEL}: {formatModelQuality(value)}
  </p>
);

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    // Find the main data point (the one with has_correction)
    const main = payload[0]?.payload;
    const hasCorrection = main?.has_correction;
    const correctionArticles = main?.correction_articles || [];
    const impactedArticles = main?.impacted_articles as ImpactedArticles | undefined;
    
    // Collect all unique impacted articles with their types
    const allImpactedArticles = new Set<string>();
    if (impactedArticles) {
      impactedArticles.promotion.forEach(id => allImpactedArticles.add(id));
      impactedArticles.substitution.forEach(id => allImpactedArticles.add(id));
      impactedArticles.outstock.forEach(id => allImpactedArticles.add(id));
      impactedArticles.manual.forEach(id => allImpactedArticles.add(id));
    }
    correctionArticles.forEach((id: string) => allImpactedArticles.add(id));
    
    const hasAnyImpact = impactedArticles && (
      impactedArticles.promotion.length > 0 ||
      impactedArticles.substitution.length > 0 ||
      impactedArticles.outstock.length > 0 ||
      impactedArticles.manual.length > 0
    );
    
    return (
      <div className={`bg-white p-3 border rounded-lg shadow-lg ${hasCorrection || hasAnyImpact ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
        <p className="font-medium text-gray-900 mb-2">{label}</p>
        
        {(hasCorrection || hasAnyImpact) && (
          <div className="mb-2">
            {(hasCorrection || hasAnyImpact) && (
              <div className="flex items-center gap-1 mb-1 px-2 py-1 bg-blue-100 rounded-md">
                <span className="text-blue-600 text-xs font-semibold">🔄 Prévision corrigée</span>
              </div>
            )}
            
            {/* Show impacted articles with color coding */}
            {impactedArticles && hasAnyImpact && (
              <div className="mt-2 space-y-1">
                {/* Manual corrections */}
                {impactedArticles.manual.length > 0 && (
                  <div className="text-xs px-2 py-1 rounded border border-orange-200 bg-orange-50">
                    <span className="font-semibold text-orange-700">Corrections manuelles:</span>
                    <span className="text-orange-600 ml-1">{impactedArticles.manual.join(', ')}</span>
                  </div>
                )}
                
                {/* Outstocked articles */}
                {impactedArticles.outstock.length > 0 && (
                  <div className="text-xs px-2 py-1 rounded border border-gray-200 bg-gray-50">
                    <span className="font-semibold text-gray-700">Ruptures de stock:</span>
                    <span className="text-gray-600 ml-1">{impactedArticles.outstock.join(', ')}</span>
                  </div>
                )}
                
                {/* Articles with both promotion and substitution */}
                {impactedArticles.promotion.filter(id => impactedArticles.substitution.includes(id)).length > 0 && (
                  <div className="text-xs px-2 py-1 rounded border border-purple-200 bg-purple-50">
                    <span className="font-semibold text-purple-700">Promotion + Substitution:</span>
                    <span className="text-purple-600 ml-1">
                      {impactedArticles.promotion.filter(id => impactedArticles.substitution.includes(id)).join(', ')}
                    </span>
                  </div>
                )}
                
                {/* Articles with promotion only */}
                {impactedArticles.promotion.filter(id => !impactedArticles.substitution.includes(id) && !impactedArticles.manual.includes(id) && !impactedArticles.outstock.includes(id)).length > 0 && (
                  <div className="text-xs px-2 py-1 rounded border border-red-200 bg-red-50">
                    <span className="font-semibold text-red-700">Promotion:</span>
                    <span className="text-red-600 ml-1">
                      {impactedArticles.promotion.filter(id => !impactedArticles.substitution.includes(id) && !impactedArticles.manual.includes(id) && !impactedArticles.outstock.includes(id)).join(', ')}
                    </span>
                  </div>
                )}
                
                {/* Articles with substitution only */}
                {impactedArticles.substitution.filter(id => !impactedArticles.promotion.includes(id) && !impactedArticles.manual.includes(id) && !impactedArticles.outstock.includes(id)).length > 0 && (
                  <div className="text-xs px-2 py-1 rounded border border-blue-200 bg-blue-50">
                    <span className="font-semibold text-blue-700">Substitution:</span>
                    <span className="text-blue-600 ml-1">
                      {impactedArticles.substitution.filter(id => !impactedArticles.promotion.includes(id) && !impactedArticles.manual.includes(id) && !impactedArticles.outstock.includes(id)).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {/* Legacy corrected articles display (for backward compatibility) */}
            {correctionArticles.length > 0 && (!impactedArticles || correctionArticles.some((id: string) => !allImpactedArticles.has(id))) && (
              <div className="text-xs text-blue-700 px-2 mt-1">
                Articles modifiés: {correctionArticles.join(', ')}
              </div>
            )}
          </div>
        )}
        
        {payload.map((entry: any, index: number) => {
          // Skip confidence entries
          if (entry.dataKey.includes('Confidence')) {
            return null;
          }
          
          // Handle historical data
          if (entry.dataKey === 'Historique') {
            return (
              <div key={index} className="mt-1">
                <p className="text-sm font-medium" style={{ color: entry.color }}>
                  Historique: {entry.value?.toLocaleString() || 'N/A'}
                </p>
              </div>
            );
          }
          
          // Handle raw predictions (show them explicitly)
          if (entry.dataKey.includes('Raw')) {
            const modelId = entry.dataKey.replace('Raw', '');
            const confidenceKey = `${entry.dataKey}Confidence`;
            const confidence = payload.find((p: any) => p.dataKey === confidenceKey)?.value;
            return (
              <div key={index} className="mt-1">
                <p className="text-sm font-medium" style={{ color: entry.color }}>
                  {modelId} (Prévisions): {entry.value?.toLocaleString() || 'N/A'}
                </p>
                <ModelQualityTooltipLine value={confidence} />
              </div>
            );
          }
          
          // Handle corrected predictions
          const rawKey = `${entry.dataKey}Raw`;
          const rawValue = payload.find((p: any) => p.dataKey === rawKey)?.value;
          const isCorrected = hasCorrection && rawValue !== undefined && entry.value !== rawValue;
          const confidenceKey = `${entry.dataKey}Confidence`;
          const confidence = payload.find((p: any) => p.dataKey === confidenceKey)?.value;
          
          return (
            <div key={index} className="mt-1">
              {/* Show corrected prediction */}
              <p className={`text-sm font-medium ${isCorrected ? 'text-blue-700' : ''}`} style={{ color: isCorrected ? '#1d4ed8' : entry.color }}>
                {entry.dataKey} (Corrigées): {entry.value?.toLocaleString() || 'N/A'}
              {isCorrected && (
                <span className="ml-1 text-xs text-blue-600">(modifiée)</span>
              )}
            </p>
            <ModelQualityTooltipLine value={confidence} />
          </div>
          );
        })}
      </div>
    );
  }
  return null;
};

const PrevisionView: React.FC<PrevisionViewProps> = ({ data, selectedArticle, selectedModel, disabledModels = new Set<string>(), onModelDataRefetch, colorSeed = 0, corrections, onCorrectionChange, filters }) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [editing, setEditing] = useState<{ [month: number]: boolean }>({});
  const [editValues, setEditValues] = useState<{ [month: number]: string }>({});
  const [pendingOperations, setPendingOperations] = useState<Set<string>>(new Set());
  const [showReminder, setShowReminder] = useState<boolean>(false);
  
  // Brush window persistence
  const DEFAULT_BRUSH_WINDOW = 12; // Default to last 12 months
  const [brushStartIndex, setBrushStartIndex] = useState<number | null>(null);
  const [brushEndIndex, setBrushEndIndex] = useState<number | null>(null);
  
  const { activeDatabase } = useDatabaseContext();
  
  // Memoize filters and disabledModels strings for stable comparison
  const filtersString = useMemo(() => JSON.stringify(filters || []), [filters]);
  const disabledModelsString = useMemo(() => Array.from(disabledModels).sort().join(','), [disabledModels]);

  // Simple correction functions that refetch data after operations
  const saveCorrection = useCallback(async (articleId: string, date: string, value: number) => {
    if (!activeDatabase) return;
    
    const operationKey = `${articleId}-${date}`;
    setPendingOperations(prev => new Set(prev).add(operationKey));
    
    try {
      await correctionsService.saveCorrection(
        activeDatabase.id,
        articleId,
        date,
        value,
        () => {
          setPendingOperations(prev => {
            const newSet = new Set(prev);
            newSet.delete(operationKey);
            return newSet;
          });
          // Trigger a refetch of the current model to get updated metadata
          if (onModelDataRefetch) {
            onModelDataRefetch();
          }
        },
        (_, error) => {
          setPendingOperations(prev => {
            const newSet = new Set(prev);
            newSet.delete(operationKey);
            return newSet;
          });
          console.error('Failed to save correction:', error);
        }
      );
    } catch (error) {
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationKey);
        return newSet;
      });
      console.error('Failed to save correction:', error);
    }
  }, [activeDatabase, onModelDataRefetch]);

  const deleteCorrection = useCallback(async (articleId: string, date: string) => {
    if (!activeDatabase) return;
    
    const operationKey = `${articleId}-${date}`;
    setPendingOperations(prev => new Set(prev).add(operationKey));
    
    try {
      await correctionsService.deleteCorrection(
        activeDatabase.id,
        articleId,
        date,
        () => {
          setPendingOperations(prev => {
            const newSet = new Set(prev);
            newSet.delete(operationKey);
            return newSet;
          });
          // Trigger a refetch of the current model to get updated metadata
          if (onModelDataRefetch) {
            onModelDataRefetch();
          }
        },
        (_, error) => {
          setPendingOperations(prev => {
            const newSet = new Set(prev);
            newSet.delete(operationKey);
            return newSet;
          });
          console.error('Failed to delete correction:', error);
        }
      );
    } catch (error) {
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(operationKey);
        return newSet;
      });
      console.error('Failed to delete correction:', error);
    }
  }, [activeDatabase, onModelDataRefetch]);

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Get table data for a specific year
  const getTableDataForYear = useCallback((year: number) => {
    const yearData = {
      historique: new Array(12).fill(null),
      previsions: new Array(12).fill(null)
    };

    // Fill with historical data for the selected year
    if (data.historicalData) {
      data.historicalData.forEach((point: TimeSeriesData) => {
        const dateObj = new Date(point.date);
        if (dateObj.getFullYear() === year) {
          const month = dateObj.getMonth();
          yearData.historique[month] = point.quantity;
        }
      });
    }

    // Fill with predictions for the selected model and year (only if model is not disabled)
    const disabledSet = disabledModels || new Set<string>();
    if (selectedModel && !disabledSet.has(selectedModel)) {
      const modelData = data.modelPredictions.find(m => m.id === selectedModel);
      if (modelData && modelData.data) {
        modelData.data.forEach((point: TimeSeriesData) => {
          const dateObj = new Date(point.date);
          if (dateObj.getFullYear() === year) {
            const month = dateObj.getMonth();
            yearData.previsions[month] = point.quantity;
          }
        });
      }
    }

    return yearData;
  }, [data.historicalData, data.modelPredictions, selectedModel, disabledModels]);

  // Prepare chart data using the backend metadata
  const chartData = useMemo(() => {
    const dataMap = new Map();
    const disabledSet = disabledModels || new Set<string>();
    const modelIds = data.modelPredictions
      .filter(model => !disabledSet.has(model.id)) // Filter out disabled models
      .map(model => model.id);

    // Add historical data
    if (data.historicalData) {
      data.historicalData.forEach((point: TimeSeriesData) => {
        const dateObj = new Date(point.date);
        const dateKey = Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), 1);
        const dateLabel = dateObj.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        
        const baseData: any = {
          dateKey,
          dateLabel,
          Historique: point.quantity,
          HistoriqueConfidence: point.confidence,
          has_correction: false,
          correction_articles: []
        };
        
        modelIds.forEach(modelId => {
          baseData[modelId] = undefined;
          baseData[`${modelId}Confidence`] = undefined;
          baseData[`${modelId}Raw`] = undefined;
          baseData[`${modelId}RawConfidence`] = undefined;
        });
        
        dataMap.set(dateKey, baseData);
      });
    }

    // Extract raw predictions (base values) from breakdown for all models (full lines)
    modelIds.forEach(modelId => {
      const modelData = data.modelPredictions.find(m => m.id === modelId);
      if (modelData && modelData.data) {
        modelData.data.forEach((point: TimeSeriesData) => {
          // Use breakdown.base_value for raw predictions (base value without manual corrections)
          const baseValue = point.breakdown?.base_value !== undefined 
            ? point.breakdown.base_value 
            : (point.quantity || 0); // Fallback to quantity if breakdown missing
          
          const dateObj = new Date(point.date);
          const dateKey = Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), 1);
          const dateLabel = dateObj.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
          
          const existingData = dataMap.get(dateKey);
          const updatedData = existingData ? {
            ...existingData,
            [`${modelId}Raw`]: baseValue,
            [`${modelId}RawConfidence`]: point.confidence,
            // Ensure correction_articles is always an array
            correction_articles: Array.isArray(existingData.correction_articles) 
              ? existingData.correction_articles 
              : [],
          } : {
            dateKey,
            dateLabel,
            Historique: undefined,
            HistoriqueConfidence: undefined,
            [`${modelId}Raw`]: baseValue,
            [`${modelId}RawConfidence`]: point.confidence,
            has_correction: false,
            correction_articles: [],
          };
          
          // Initialize other model keys if new data
          if (!existingData) {
            modelIds.forEach(otherModelId => {
              if (otherModelId !== modelId) {
                updatedData[`${otherModelId}Raw`] = undefined;
                updatedData[`${otherModelId}RawConfidence`] = undefined;
              }
              updatedData[otherModelId] = undefined;
              updatedData[`${otherModelId}Confidence`] = undefined;
            });
          }
          
          dataMap.set(dateKey, updatedData);
        });
      }
    });

    // Add predictions for all models with corrections applied (dotted lines)
    modelIds.forEach(modelId => {
      const modelData = data.modelPredictions.find(m => m.id === modelId);
      if (modelData && modelData.data) {
        modelData.data.forEach((point: TimeSeriesData) => {
          const dateObj = new Date(point.date);
          const dateKey = Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), 1);
          const dateLabel = dateObj.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
          
          // Use backend metadata for correction status
          const hasCorrection = point.has_correction;
          const correctionArticles = point.corrected_articles || [];
          const impactedArticles = point.impacted_articles;
          
          const existingData = dataMap.get(dateKey);
          const existingCorrectionArticles = Array.isArray(existingData?.correction_articles) 
            ? existingData.correction_articles 
            : [];
          const existingImpactedArticles = existingData?.impacted_articles;
          
          // Merge impacted articles
          let mergedImpactedArticles: ImpactedArticles | undefined = undefined;
          if (impactedArticles || existingImpactedArticles) {
            mergedImpactedArticles = {
              promotion: Array.from(new Set([
                ...(existingImpactedArticles?.promotion || []),
                ...(impactedArticles?.promotion || [])
              ])),
              substitution: Array.from(new Set([
                ...(existingImpactedArticles?.substitution || []),
                ...(impactedArticles?.substitution || [])
              ])),
              outstock: Array.from(new Set([
                ...(existingImpactedArticles?.outstock || []),
                ...(impactedArticles?.outstock || [])
              ])),
              manual: Array.from(new Set([
                ...(existingImpactedArticles?.manual || []),
                ...(impactedArticles?.manual || [])
              ]))
            };
          }
          
          const updatedData = existingData ? {
            ...existingData,
            [modelId]: point.quantity,
            [`${modelId}Confidence`]: point.confidence,
            has_correction: hasCorrection || existingData.has_correction,
            correction_articles: Array.from(new Set([...existingCorrectionArticles, ...correctionArticles])),
            impacted_articles: mergedImpactedArticles || existingImpactedArticles
          } : {
            dateKey,
            dateLabel,
            Historique: undefined,
            HistoriqueConfidence: undefined,
            [modelId]: point.quantity,
            [`${modelId}Confidence`]: point.confidence,
            has_correction: hasCorrection,
            correction_articles: correctionArticles,
            impacted_articles: impactedArticles
          };
          
          // Initialize other model keys if new data
          if (!existingData) {
            modelIds.forEach(otherModelId => {
              if (otherModelId !== modelId) {
                updatedData[otherModelId] = undefined;
                updatedData[`${otherModelId}Confidence`] = undefined;
              }
              updatedData[`${otherModelId}Raw`] = undefined;
              updatedData[`${otherModelId}RawConfidence`] = undefined;
            });
          }
          
          dataMap.set(dateKey, updatedData);
        });
      }
    });

    const result = Array.from(dataMap.values()).sort((a: any, b: any) => {
      return (a.dateKey ?? 0) - (b.dateKey ?? 0);
    });
    
    // Ensure all data points have all model keys (like the old version did)
    // Recharts needs the keys to exist in ALL data points, even if undefined
    result.forEach((point: any) => {
      modelIds.forEach(modelId => {
        // Ensure both raw and corrected keys exist in every data point
        // Initialize to undefined if not present (recharts handles undefined well)
        if (!(`${modelId}Raw` in point)) {
          point[`${modelId}Raw`] = undefined;
        }
        if (!(modelId in point)) {
          point[modelId] = undefined;
        }
        // Ensure confidence keys exist too
        if (!(`${modelId}RawConfidence` in point)) {
          point[`${modelId}RawConfidence`] = undefined;
        }
        if (!(`${modelId}Confidence` in point)) {
          point[`${modelId}Confidence`] = undefined;
        }
      });
    });
    
    return result;
  }, [data.modelPredictions, data.historicalData, disabledModels]);

  // Calculate default brush indices
  const defaultBrushIndices = useMemo(() => {
    const disabledSet = disabledModels || new Set<string>();
    const modelIds = data.modelPredictions
      .filter(model => !disabledSet.has(model.id))
      .map(model => model.id);
    
    if (chartData.length === 0) {
      return { startIndex: 0, endIndex: 0 };
    }
    
    // Find the range that actually has data
    let firstDataIndex = -1;
    let lastDataIndex = -1;
    
    chartData.forEach((point: any, index: number) => {
      const hasHistorical = point.Historique !== undefined && point.Historique !== null;
      const hasAnyModelData = modelIds.some(modelId => 
        (point[modelId] !== undefined && point[modelId] !== null) ||
        (point[`${modelId}Raw`] !== undefined && point[`${modelId}Raw`] !== null)
      );
      
      if (hasHistorical || hasAnyModelData) {
        if (firstDataIndex === -1) firstDataIndex = index;
        lastDataIndex = index;
      }
    });
    
    if (firstDataIndex === -1 || lastDataIndex === -1) {
      // No data found, show all
      const startIndex = Math.max(0, chartData.length - DEFAULT_BRUSH_WINDOW);
      const endIndex = chartData.length - 1;
      return { startIndex, endIndex };
    }
    
    // Show data range, but limit to DEFAULT_BRUSH_WINDOW if it's too wide
    const dataRange = lastDataIndex - firstDataIndex + 1;
    const windowSize = Math.min(dataRange, DEFAULT_BRUSH_WINDOW);
    const startIndex = Math.max(firstDataIndex, lastDataIndex - windowSize + 1);
    const endIndex = lastDataIndex;
    
    return { startIndex, endIndex };
  }, [DEFAULT_BRUSH_WINDOW, chartData, data.modelPredictions, disabledModels]);

  // Get current brush indices (use saved state or default)
  const currentBrushIndices = useMemo(() => {
    if (brushStartIndex !== null && brushEndIndex !== null) {
      // Use saved brush position, but ensure it's within bounds
      const startIndex = Math.max(0, Math.min(brushStartIndex, chartData.length - 1));
      const endIndex = Math.max(startIndex, Math.min(brushEndIndex, chartData.length - 1));
      return { startIndex, endIndex };
    }
    
    return defaultBrushIndices;
  }, [brushStartIndex, brushEndIndex, chartData.length, defaultBrushIndices]);

  // Handle brush change
  const handleBrushChange = useCallback((brushData: any) => {
    if (brushData && brushData.startIndex !== undefined && brushData.endIndex !== undefined) {
      setBrushStartIndex(brushData.startIndex);
      setBrushEndIndex(brushData.endIndex);
    }
  }, []);

  // Prepare table data
  const tableData = useMemo(() => {
    return getTableDataForYear(selectedYear);
  }, [getTableDataForYear, selectedYear]);

  const years = useMemo(() => {
    const allDates: number[] = [];

    // Ajouter les dates des données historiques
    if (Array.isArray(data.historicalData)) {
      data.historicalData.forEach(point => {
        allDates.push(new Date(point.date).getFullYear());
      });
    }
    // Ajouter les dates des prédictions seulement si un modèle est sélectionné
    if (selectedModel && Array.isArray(data.modelPredictions)) {
      data.modelPredictions.forEach(model => {
        if (model.id === selectedModel && Array.isArray(model.data)) {
          model.data.forEach(point => {
            allDates.push(new Date(point.date).getFullYear());
          });
        }
      });
    }
    
    // Si aucune date, retourner l'année courante
    if (allDates.length === 0) {
      return [new Date().getFullYear()];
    }
    
    // Retourner les années uniques triées
    return Array.from(new Set(allDates)).sort();
  }, [data.historicalData, data.modelPredictions, selectedModel]);





  // Check if any model has errors
  const hasModelErrors = data.modelPredictions.some(model => model.error);

  // Create comprehensive chart data using the new approach

  if (data.isLoading) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl p-6 shadow-sm" style={{ height: '60vh' }}>
          <div className="flex justify-center items-center h-full">
            <div className="text-gray-500">Chargement des données...</div>
          </div>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Chargement des données...</div>
        </div>
      </div>
    );
  }

  // Don't hide chart for global errors, only for loading errors
  if (data.error && !hasModelErrors) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl p-6 shadow-sm" style={{ height: '60vh' }}>
          <div className="flex justify-center items-center h-full">
            <div className="text-red-500">Une erreur est survenue lors du chargement des données</div>
          </div>
        </div>
        <div className="text-red-500 p-4">{String(data.error)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Model Selector */}
      {/* This section is now handled by the parent component */}

      {/* Pending operations indicator */}
      {pendingOperations.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="animate-spin h-4 w-4 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <div className="ml-2">
              <span className="text-sm font-medium text-blue-800">
                Synchronisation en cours...
              </span>
              <span className="text-sm text-blue-700 ml-1">
                Les corrections sont en cours de sauvegarde
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Reminder popup for multiple articles */}
      {showReminder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowReminder(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Information
                </h3>
                <p className="text-sm text-gray-700">
                  Les corrections ne peuvent être modifiées que lorsqu'un seul article est sélectionné.
                </p>
              </div>
              <button
                onClick={() => setShowReminder(false)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowReminder(false)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm font-medium"
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="bg-white rounded-xl p-6 shadow-sm" style={{ height: '50vh', minHeight: '400px' }}>
        {chartData.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <div className="text-gray-500">Aucune donnée à afficher</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 30, bottom: 10 }}
              syncId="prevision-chart"
            >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fill: '#6b7280', fontSize: 12 }}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 12 }}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top"
              height={50}
              wrapperStyle={{ paddingBottom: 10 }}
              formatter={(value) => (
                <span className="text-sm text-gray-600">{value}</span>
              )}
            />

            {/* Historical data line */}
            <Line
              type="monotone"
              dataKey="Historique"
              stroke={MODEL_COLORS.historical}
              strokeWidth={2.5}
              dot={{
                r: 6,
                fill: MODEL_COLORS.historical,
                stroke: "#fff",
                strokeWidth: 2
              }}
              activeDot={false}
            />

            {/* Model prediction lines - Raw (full line) and Corrected (dotted line) */}
            {data.modelPredictions
              .filter(model => !disabledModels.has(model.id)) // Filter out disabled models
              .flatMap(model => {
                // Check if we have data with breakdown (for raw) or corrected data
                const hasData = model.data && model.data.length > 0;
                const hasBreakdown = hasData && model.data.some((pt: TimeSeriesData) => pt.breakdown?.base_value !== undefined);
                const hasCorrectedData = hasData;
                
                // Skip if no data at all
                if (!hasData) {
                  return [];
                }
                
                const isSelected = selectedModel === model.id;
                const color = getModelColor(model.id, colorSeed);
                
                // Create dataKey strings explicitly
                const rawDataKey = `${model.id}Raw`;
                const correctedDataKey = model.id;
                
                // Return array of Line components with unique keys
                const lines = [];
                
                // Raw predictions line (solid line) - using breakdown.base_value
                if (hasBreakdown) {
                  lines.push(
                    <Line
                      key={`${model.id}-raw`}
                      type="monotone"
                      dataKey={rawDataKey}
                      stroke={color}
                      strokeWidth={isSelected ? 3 : 2}
                      dot={{
                        r: isSelected ? 7 : 5,
                        fill: color,
                        stroke: "#fff",
                        strokeWidth: 2
                      }}
                      activeDot={isSelected ? { r: 8, fill: color, stroke: '#fff', strokeWidth: 2 } : false}
                      name={`${model.name} (Prévisions)`}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  );
                }
                
                // Corrected predictions line (dotted line)
                if (hasCorrectedData) {
                  lines.push(
                    <Line
                      key={`${model.id}-corrected`}
                      type="monotone"
                      dataKey={correctedDataKey}
                      stroke={color}
                      strokeWidth={isSelected ? 3 : 2}
                      strokeDasharray="5 5"
                      dot={(dotProps) => <CustomDot {...dotProps} dataKey={correctedDataKey} r={isSelected ? 7 : 5} fill={color} stroke="#fff" />}
                      activeDot={isSelected ? { r: 8, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 } : false}
                      name={`${model.name} (Corrigées)`}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  );
                }
                
                return lines;
              })}


            <Brush
              dataKey="dateLabel"
              height={80}
              stroke="#c7d2fe"
              travellerWidth={12}
              startIndex={currentBrushIndices.startIndex}
              endIndex={currentBrushIndices.endIndex}
              onChange={handleBrushChange}
              fill="#fff"
            >
              <LineChart data={chartData}>
                <Line
                  type="monotone"
                  dataKey="Historique"
                  stroke={MODEL_COLORS.historical}
                  strokeWidth={1}
                  dot={false}
                />
                {data.modelPredictions
                  .filter(model => !disabledModels.has(model.id)) // Filter out disabled models
                  .flatMap(model => {
                    const hasData = model.data && model.data.length > 0;
                    const hasBreakdown = hasData && model.data.some((pt: TimeSeriesData) => pt.breakdown?.base_value !== undefined);
                    const hasCorrectedData = hasData;
                    
                    // Skip if no data at all
                    if (!hasData) {
                      return [];
                    }
                    
                    const color = getModelColor(model.id, colorSeed);
                    
                    // Return array of Line components with unique keys
                    const lines = [];
                    
                    // Raw predictions line (solid line) - using breakdown.base_value
                    if (hasBreakdown) {
                      lines.push(
                        <Line
                          key={`${model.id}-raw-brush`}
                          type="monotone"
                          dataKey={`${model.id}Raw`}
                          stroke={color}
                          strokeWidth={1}
                          dot={false}
                          connectNulls={false}
                        />
                      );
                    }
                    
                    // Corrected predictions line (dotted line)
                    if (hasCorrectedData) {
                      lines.push(
                        <Line
                          key={`${model.id}-corrected-brush`}
                          type="monotone"
                          dataKey={model.id}
                          stroke={color}
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          dot={false}
                          connectNulls={false}
                        />
                      );
                    }
                    
                    return lines;
                  })}
              </LineChart>
            </Brush>
          </LineChart>
        </ResponsiveContainer>
        )}
      </div>

      {/* Table */}
      <div className="w-full bg-white rounded-xl p-6 shadow-sm" style={{ height: '30vh' }}>
        <div className="flex justify-between items-center mb-2">
          <button
            onClick={() => setSelectedYear(prev => Math.max(...years.filter(y => y < prev)))}
            disabled={selectedYear <= Math.min(...years)}
            className="p-2 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <span className="text-lg font-medium">{selectedYear}</span>
          <button
            onClick={() => setSelectedYear(prev => Math.min(...years.filter(y => y > prev)))}
            disabled={selectedYear >= Math.max(...years)}
            className="p-2 rounded hover:bg-gray-100 disabled:opacity-50"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto h-[calc(100%-40px)]">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-4 py-2 text-sm font-medium text-gray-500 text-left w-32">Type</th>
                {months.map(month => (
                  <th key={month} className="px-4 py-2 text-sm font-medium text-gray-500">
                    {month}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white">
              <tr className="border-t">
                <td className="px-4 py-2 text-sm text-gray-500 font-medium">
                  Données historiques
                </td>
                {tableData.historique.map((value, idx) => (
                  <td key={idx} className="px-4 py-2 text-sm text-center">
                    {value?.toLocaleString() || '-'}
                  </td>
                ))}
              </tr>
              {/* Prévisions row - Always shows original model predictions (non-editable) */}
              <tr className="border-t">
                <td className="px-4 py-2 text-sm text-gray-500 font-medium">
                  {selectedModel && !disabledModels.has(selectedModel) ? 'Prévisions' : 'Prévisions (aucun modèle sélectionné)'}
                </td>
                {months.map((_, idx) => {
                  // Show message when no model is selected or selected model is disabled
                  if (!selectedModel || disabledModels.has(selectedModel)) {
                    return (
                      <td key={idx} className="px-4 py-2 text-sm text-center text-gray-400">
                        -
                      </td>
                    );
                  }
                  
                  // Get raw prediction value (base value from breakdown)
                  let rawPredictionValue: number | null = null;
                  let breakdown: PredictionBreakdown | undefined = undefined;
                  
                  if (selectedModel) {
                    const modelData = data.modelPredictions.find(m => m.id === selectedModel);
                    if (modelData && modelData.data) {
                      const point = modelData.data.find((pt: TimeSeriesData) => {
                        const date = new Date(pt.date);
                        return date.getFullYear() === selectedYear && date.getMonth() === idx;
                      });
                      if (point) {
                        // Use breakdown.base_value for raw prediction (base value without manual corrections)
                        rawPredictionValue = point.breakdown?.base_value !== undefined 
                          ? point.breakdown.base_value 
                          : (point.quantity ?? null); // Fallback to quantity if breakdown missing
                        breakdown = point.breakdown;
                      }
                    }
                  }
                  
                  const cellContent = rawPredictionValue !== null ? (
                    <span>{rawPredictionValue.toLocaleString()}</span>
                  ) : (
                    <span className="text-gray-300">-</span>
                  );
                  
                  // Create breakdown tooltip content
                  const getBreakdownTooltip = () => {
                    // When multiple articles are selected, breakdown is not meaningful (aggregated data)
                    if (!selectedArticle) {
                      return 'Le détail du calcul est disponible uniquement pour un article sélectionné';
                    }
                    
                    if (!breakdown) {
                      return `Valeur finale: ${rawPredictionValue?.toLocaleString() || 0}`;
                    }
                    
                    if (breakdown.is_outstocked) {
                      return `Rupture de stock (Base: 0)`;
                    }
                    
                    // Breakdown shows: base + promotions + substitutions (+ manual corrections if any) = final value
                    const promotionText = breakdown.promotion_amount !== 0 
                      ? `Promotion: ${breakdown.promotion_amount > 0 ? '+' : ''}${breakdown.promotion_amount.toLocaleString()}` 
                      : 'Promotion: 0';
                    const substitutionText = breakdown.substitution_amount !== 0
                      ? `Substitution: ${breakdown.substitution_amount > 0 ? '+' : ''}${breakdown.substitution_amount.toLocaleString()}`
                      : 'Substitution: 0';
                    const manualText = breakdown.is_manually_corrected && breakdown.manual_correction_amount !== undefined && breakdown.manual_correction_amount !== 0
                      ? ` | Correction manuelle: ${breakdown.manual_correction_amount > 0 ? '+' : ''}${breakdown.manual_correction_amount.toLocaleString()}`
                      : '';
                    const total = breakdown.base_value + breakdown.promotion_amount + breakdown.substitution_amount + (breakdown.manual_correction_amount || 0);
                    
                    // Show breakdown of the final computed value
                    return `Base: ${breakdown.base_value.toLocaleString()} | ${promotionText} | ${substitutionText}${manualText} | Total: ${total.toLocaleString()}`;
                  };
                  
                  return (
                    <td key={idx} className="px-4 py-2 text-sm text-center text-black">
                      {rawPredictionValue !== null ? (
                        <MuiTooltip 
                          title={
                            <div className="text-xs">
                              {getBreakdownTooltip()}
                            </div>
                          }
                          arrow
                        >
                          <span className="cursor-help">{rawPredictionValue.toLocaleString()}</span>
                        </MuiTooltip>
                      ) : (
                        cellContent
                      )}
                    </td>
                  );
                })}
              </tr>
              
              {/* Corrections row - Shows corrections, editable only when single article selected */}
              <tr className="border-t">
                <td className="px-4 py-2 text-sm text-gray-500 font-medium">
                  Corrections
                </td>
                {months.map((_, idx) => {
                  if (!selectedModel || disabledModels.has(selectedModel)) {
                    return (
                      <td key={idx} className="px-4 py-2 text-sm text-center text-gray-400">
                        -
                      </td>
                    );
                  }

                  // Get correction value for this month
                  let correctionValue: number | null = null;
                  let hasCorrection = false;
                  
                  if (selectedArticle && selectedModel) {
                    const modelData = data.modelPredictions.find(m => m.id === selectedModel);
                    if (modelData && modelData.data) {
                      const point = modelData.data.find((pt: TimeSeriesData) => {
                        const date = new Date(pt.date);
                        return date.getFullYear() === selectedYear && date.getMonth() === idx;
                      });
                      if (point && point.has_correction) {
                        hasCorrection = true;
                        correctionValue = point.quantity ?? null;
                      }
                    }
                  } else if (!selectedArticle) {
                    // Multiple articles: get aggregated value (sum of corrections + sum of predictions without corrections)
                    const modelData = data.modelPredictions.find(m => m.id === selectedModel);
                    if (modelData && modelData.data) {
                      const point = modelData.data.find((pt: TimeSeriesData) => {
                        const date = new Date(pt.date);
                        return date.getFullYear() === selectedYear && date.getMonth() === idx;
                      });
                      if (point) {
                        // The backend already aggregates: corrections + predictions without corrections
                        // So we can use the quantity directly
                        correctionValue = point.quantity ?? null;
                        hasCorrection = point.has_correction || false;
                      }
                    }
                  }

                  // Single article: allow editing/deleting
                  if (selectedArticle) {
                    const isEditing = editing[idx];
                    const value =
                      typeof editValues[idx] === 'string'
                        ? editValues[idx]
                        : correctionValue?.toString() || '';
                    
                    return (
                      <td key={idx} className="px-4 py-2 text-sm text-center">
                        {isEditing ? (
                          <input
                            type="number"
                            value={value ?? ''}
                            onChange={e => {
                              setEditValues(prev => ({ ...prev, [idx]: e.target.value }));
                            }}
                            onBlur={() => {
                              setEditing(prev => ({ ...prev, [idx]: false }));
                              // Save the correction if there's a valid value
                              const currentValue = editValues[idx];
                              if (currentValue && !isNaN(parseFloat(currentValue))) {
                                const date = new Date(Date.UTC(selectedYear, idx, 1)).toISOString().slice(0, 10);
                                saveCorrection(String(selectedArticle.id), date, parseFloat(currentValue));
                              } else if (hasCorrection && (!currentValue || currentValue === '')) {
                                // If there was a correction and now empty, delete it
                                const date = new Date(Date.UTC(selectedYear, idx, 1)).toISOString().slice(0, 10);
                                deleteCorrection(String(selectedArticle.id), date);
                              }
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.currentTarget.blur();
                              }
                              if (e.key === 'Escape') {
                                setEditing(prev => ({ ...prev, [idx]: false }));
                                setEditValues(prev => ({ ...prev, [idx]: '' }));
                              }
                            }}
                            className="w-16 border rounded px-1 py-0.5 text-center"
                            autoFocus
                            placeholder="-"
                          />
                        ) : (
                          <span
                            onClick={() => {
                              setEditing(prev => ({ ...prev, [idx]: true }));
                              setEditValues(prev => ({ 
                                ...prev, 
                                [idx]: correctionValue?.toString() || ''
                              }));
                            }}
                            className="cursor-pointer select-none flex items-center justify-center gap-1 text-blue-600 hover:text-blue-800"
                          >
                            {correctionValue !== null && correctionValue !== undefined ? (
                              <>
                                {correctionValue.toLocaleString()}
                                <button
                                  type="button"
                                  className="ml-1 text-gray-400 hover:text-red-500"
                                  title="Supprimer la correction"
                                  onClick={async e => {
                                    e.stopPropagation();
                                    const date = new Date(Date.UTC(selectedYear, idx, 1)).toISOString().slice(0, 10);
                                    deleteCorrection(String(selectedArticle.id), date);
                                  }}
                                >
                                  <TrashIcon className="h-4 w-4 inline" />
                                </button>
                              </>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </span>
                        )}
                      </td>
                    );
                  } else {
                    // Multiple articles selected: show aggregated value (sum of corrections + predictions without corrections)
                    const correctedArticlesList: string[] = [];
                    if (hasCorrection) {
                      const modelData = data.modelPredictions.find(m => m.id === selectedModel);
                      if (modelData && modelData.data) {
                        const point = modelData.data.find((pt: TimeSeriesData) => {
                          const date = new Date(pt.date);
                          return date.getFullYear() === selectedYear && date.getMonth() === idx;
                        });
                        if (point && point.corrected_articles) {
                          correctedArticlesList.push(...point.corrected_articles);
                        }
                      }
                    }

                    return (
                      <td 
                        key={idx} 
                        className={`px-4 py-2 text-sm text-center ${hasCorrection ? 'text-blue-600' : 'text-gray-400'} cursor-pointer`}
                        onClick={() => {
                          setShowReminder(true);
                          setTimeout(() => setShowReminder(false), 3000);
                        }}
                      >
                        {correctionValue !== null && correctionValue !== undefined ? (
                          <MuiTooltip
                            title={
                              <div>
                                {hasCorrection && correctedArticlesList.length > 0 && (
                                  <div className="mb-2">
                                    <div className="text-xs font-semibold mb-1">🔄 Prévision corrigée</div>
                                    <div className="text-xs">
                                      Articles modifiés: {correctedArticlesList.join(', ')}
                                    </div>
                                  </div>
                                )}
                                <div className="text-xs">
                                  Les corrections ne peuvent être modifiées que lorsqu'un seul article est sélectionné.
                                </div>
                              </div>
                            }
                            arrow
                            placement="top"
                          >
                            <span className={hasCorrection ? 'text-blue-600 font-medium' : 'text-gray-600'}>
                              {correctionValue.toLocaleString()}
                              {hasCorrection && (
                                <span className="ml-1 text-xs">●</span>
                              )}
                            </span>
                          </MuiTooltip>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    );
                  }
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PrevisionView; 